/**
 * Utility for generating deterministic colors from strings
 */

export interface ColorResult {
  backgroundColor: string;
  textColor: string;
}

export class ColorGenerator {
  // Predefined set of background colors with good visual variety
  private static readonly COLOR_PALETTE = [
    '#1976d2', // Material Blue
    '#388e3c', // Material Green
    '#f57c00', // Material Orange
    '#7b1fa2', // Material Purple
    '#c62828', // Material Red
    '#00796b', // Material Teal
    '#455a64', // Material Blue Grey
    '#e91e63', // Material Pink
    '#ff5722', // Material Deep Orange
    '#9c27b0', // Material Purple
    '#673ab7', // Material Deep Purple
    '#3f51b5', // Material Indigo
    '#2196f3', // Material Light Blue
    '#00bcd4', // Material Cyan
    '#009688', // Material Teal
    '#4caf50', // Material Light Green
    '#8bc34a', // Material Lime
    '#cddc39', // Material Lime
    '#ffeb3b', // Material Yellow
    '#ffc107', // Material Amber
    '#ff9800', // Material Orange
    '#607d8b', // Material Blue Grey
  ];

  /**
   * Generate a deterministic color based on input string
   * @param input - The input string to generate color from
   * @returns Color result with background and text colors
   */
  public static generateColor(input: string): ColorResult {
    if (!input || input.trim().length === 0) {
      // Default fallback color
      return {
        backgroundColor: '#757575',
        textColor: '#ffffff',
      };
    }

    // Normalize the input to ensure consistency
    const normalizedInput = input.trim().toLowerCase();

    // Generate hash and map to color palette
    const hash = this.hashString(normalizedInput);
    const colorIndex = hash % this.COLOR_PALETTE.length;
    const backgroundColor = this.COLOR_PALETTE.at(colorIndex) ?? '#757575';

    // Calculate the optimal text color dynamically for better contrast
    const optimalTextColor = this.getOptimalTextColor(backgroundColor);

    return {
      backgroundColor,
      textColor: optimalTextColor,
    };
  }

  /**
   * Calculate contrast ratio between two colors
   * @param color1 - First color in hex format
   * @param color2 - Second color in hex format
   * @returns Contrast ratio
   */
  public static getContrastRatio(color1: string, color2: string): number {
    const l1 = this.getLuminance(color1);
    const l2 = this.getLuminance(color2);
    const lighter = Math.max(l1, l2);
    const darker = Math.min(l1, l2);

    return (lighter + 0.05) / (darker + 0.05);
  }

  /**
   * Determine if the given background color should use white or black text
   * @param backgroundColor - Background color in hex format
   * @returns Optimal text color
   */
  public static getOptimalTextColor(backgroundColor: string): string {
    const whiteContrast = this.getContrastRatio(backgroundColor, '#ffffff');
    const blackContrast = this.getContrastRatio(backgroundColor, '#000000');

    return whiteContrast > blackContrast ? '#ffffff' : '#000000';
  }

  /**
   * Simple hash function to convert string to number
   * @param string_ - The input string to hash
   * @returns Hash value as number
   */
  private static hashString(string_: string): number {
    let hash = 0;
    if (string_.length === 0) {
      return hash;
    }

    for (let index = 0; index < string_.length; index++) {
      const character = string_.codePointAt(index) || 0;
      hash = (hash * 31 + character) % Number.MAX_SAFE_INTEGER;
    }

    return Math.abs(hash);
  }

  /**
   * Get luminance of a hex color (for contrast calculation)
   * @param hex - Hex color string
   * @returns Luminance value
   */
  private static getLuminance(hex: string): number {
    const rgb = this.hexToRgb(hex);
    if (!rgb) {
      return 0;
    }

    // Convert sRGB to linear RGB
    const linearRgb = [rgb.r, rgb.g, rgb.b].map((color) => {
      const normalizedColor = color / 255;
      return normalizedColor <= 0.039_28 ? normalizedColor / 12.92 : ((normalizedColor + 0.055) / 1.055) ** 2.4;
    });

    // Calculate relative luminance
    return 0.2126 * linearRgb[0] + 0.7152 * linearRgb[1] + 0.0722 * linearRgb[2];
  }

  /**
   * Convert hex color to RGB
   * @param hex - Hex color string
   * @returns RGB object or null if invalid
   */
  private static hexToRgb(hex: string): {
    b: number;
    g: number;
    r: number;
  } | null {
    const result = /^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i.exec(hex);
    return result
      ? {
          b: Number.parseInt(result[3], 16),
          g: Number.parseInt(result[2], 16),
          r: Number.parseInt(result[1], 16),
        }
      : null;
  }
}
